// Mirrors internal/repos.Manifest/Entry and the GET /api/repos response
// shape in internal/server (handleReposList). Keep in sync with the Go
// structs' json tags.

// LAST_REPO_STORAGE_KEY persists the repo a user explicitly switched to
// (AppShell's selector), consulted only when landing on the bare root "/" —
// visiting a specific deep link never overwrites this, only a deliberate
// switch does, so a shared link never silently changes someone else's
// remembered preference. Storing a repo key here is fine under this app's
// "no long-lived token in localStorage" rule (M2, auth.ts) — that rule is
// about the OFLIC access token, not app preference state.
export const LAST_REPO_STORAGE_KEY = "atlas-control:lastRepo";

// RepoManifest declares which content areas a repo actually has —
// note there is no "hasProgress": internal/progress.Service is
// architecturally bound to the default repo only (register-diff-driven,
// and only atlas has a register), so Progress is never gated by this
// manifest — see NAV_SECTIONS in AppShell.tsx.
export interface RepoManifest {
  hasRegister: boolean;
  hasBits: boolean;
  hasDesign: boolean;
  hasADR: boolean;
  hasOperations: boolean;
}

// RepoSync mirrors snapshot.Status, omitted entirely by the backend (not
// sent as null) when this repo has no store at all.
export interface RepoSync {
  ok: boolean;
  hasSnapshot: boolean;
  ref?: string;
  sha?: string;
  ageSeconds?: number;
  lastError?: string;
}

export interface RepoEntry {
  key: string;
  displayName: string;
  // designAreaLabel is the nav label for this repo's design/reference
  // catalogue — "Design library" for atlas/cortex-chat, "Reference
  // library" for cortex-core (compliance/patent/phases/tasks folded into
  // the same catalogue machinery). Always use this instead of a hardcoded
  // "Design library" string.
  designAreaLabel: string;
  owner: string;
  repo: string;
  ref: string;
  root: string;
  manifest: RepoManifest;
  default: boolean;
  sync?: RepoSync;
}

export interface ReposResponse {
  repos: RepoEntry[];
  defaultRepo: string;
}

// SECTION_MANIFEST_KEY maps a nav section to the manifest boolean that
// gates it. "progress" is deliberately absent — it's bound to the default
// repo only (see RepoManifest's doc comment), not manifest-gated.
export const SECTION_MANIFEST_KEY: Record<string, keyof RepoManifest> = {
  capabilities: "hasRegister",
  adrs: "hasADR",
  runbooks: "hasOperations",
  design: "hasDesign",
  bits: "hasBits",
};

// SECTION_PRIORITY is the fallback order used to pick a landing section for
// a repo with no register (so no dashboard) — the first section its
// manifest actually has, in this fixed order.
export const SECTION_PRIORITY = ["capabilities", "adrs", "runbooks", "design", "bits"];

export function hasSection(manifest: RepoManifest, section: string): boolean {
  const key = SECTION_MANIFEST_KEY[section];
  return key ? manifest[key] : false;
}

// landingPath picks where switching to (or landing fresh on) a repo should
// go: its dashboard if it has a register, else the first section its
// manifest has in SECTION_PRIORITY order, else the bare repo path.
export function landingPath(entry: RepoEntry): string {
  if (entry.manifest.hasRegister) return `/${entry.key}`;
  const section = SECTION_PRIORITY.find((s) => hasSection(entry.manifest, s));
  return section ? `/${entry.key}/${section}` : `/${entry.key}`;
}
